import { Component, OnInit, Input, OnChanges, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { head, eq, find } from 'lodash';
import {
  AuthenticationService,
  AuthorService,
  ProjectService,
  SearchService,
  UserLabelService,
} from 'services/index';
import { FacetSearch, Notification, Search } from 'app/models';
import { Paginator } from 'primeng/paginator';
import { OutletService } from 'app/services';
import * as actions from 'redux/actions';
import { Router } from '@angular/router';
import { MAP_URL } from 'app/pages/topics/constants';
import {
  Content,
  SPINNER_ICON,
  STAR_ICON,
  STAR_ICON_FILL,
} from 'app/constants';

@Component({
  selector: 'facet-author-content',
  templateUrl: './facet-author-content.component.html',
  styleUrls: ['./facet-author-content.component.scss'],
})
export class FacetAuthorContentComponent implements OnInit, OnChanges {
  @Input() contentData: any;
  public searchBody: Search;
  public authorContentPage: number;
  public authorContentTotalPages: number;
  public authorContentPageSize: number;
  public authorContentTotal: number;
  public currentPage: number;
  public loading: boolean;

  public author: any;
  public contentItems: any[] = [];
  public sortSelected: string;
  public hideFollowing: boolean;
  public starToggle: string;
  public authorsOutletsList: any[];
  public project: any;
  public isAuthorSaved: boolean;
  public actionType: string;
  public savedAuthor: any;
  public categories: any[] = [];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  @ViewChild('pp', { static: false }) paginator: Paginator;
  public parentName: Content;

  constructor(
    private authorService: AuthorService,
    private searchService: SearchService,
    private outletService: OutletService,
    private auth: AuthenticationService,
    private router: Router,
    private store: Store<AppState>,
    private userLabelService: UserLabelService
  ) {
    this.parentName = Content.Author;
    this.sortSelected = 'recent';
    this.loading = false;
    this.starToggle = STAR_ICON;
    this.isAuthorSaved = false;
    this.authorsOutletsList = [];
    this.project = null;
    this.actionType = null;
    this.searchBody = null;
  }

  ngOnInit() {
    this.hideFollowing = false;
    this.authorContentPage = 1;
    this.currentPage = 0;

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store
      .select('authorsOutletsList')
      .subscribe(
        ({
          authors,
          isAddingAuthorOutlet,
          isDeletingAuthorOutlet,
          hasAuthorOutletDeleted,
          hasAuthorOutletAdded,
          errors,
        }) => {
          this.authorsOutletsList = authors;

          if (isAddingAuthorOutlet || isDeletingAuthorOutlet) {
            this.starToggle = SPINNER_ICON;
          }

          if (hasAuthorOutletAdded) {
            this.setAuthorSaved();
          }

          if (hasAuthorOutletDeleted) {
            this.setAuthorSaved();
          }

          if (errors && errors.message) {
            this.store.dispatch(
              actions.showNotification({
                notify: new Notification(
                  `Error ${this.actionType} author to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setAuthorSaved();
            setTimeout(() => {
              this.store.dispatch(actions.initAuthorOutletToProjectState());
            }, 500);
          }
        }
      );
  }

  ngOnChanges(changes: any) {
    if (!changes.contentData || !changes.contentData.currentValue) {
      return;
    }
    const { author, contentItems, facetSearch } =
      changes.contentData.currentValue;
    if (!author || !contentItems) {
      return;
    }

    const items = contentItems.content || [];
    const firstContentItem = head(items);
    const outlet = firstContentItem?.outlet || author.outletNames;
    const outletId = firstContentItem?.outletId || author.outletId;
    this.author = {
      ...author,
      outletId,
      outlet,
      picture: this.authorService.getAuthorPicture(author.id),
    };

    this.authorContentTotalPages = contentItems.totalPages;
    this.authorContentTotal = contentItems.totalElements;
    this.contentItems = items;
    this.authorContentPageSize = facetSearch?.size || contentItems.size || 25;
    this.currentPage = facetSearch?.page || 0;
    this.setAuthorSaved();
    this.resetPaginator();
  }

  public goToExploreRelatedCoverage(outlet) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/author/${outlet.id}`])
    );
    window.open(url, '_blank');
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.authorContentPage = 1;
    this.currentPage = 0;
    this.resetPaginator();
    this.getAuthorContent();
  }

  getPage(e: any) {
    this.authorContentPage = e.page + 1;
    this.currentPage = e.page;
    this.getAuthorContent();
  }

  getAuthorContent() {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
      }),
      page: this.currentPage,
      size: this.authorContentPageSize,
      sort: this.sortSelected,
    });

    this.searchService.searchContent(facetSearch).subscribe((response) => {
      this.authorContentTotalPages = response.totalPages;
      this.authorContentTotal = response.totalElements;
      this.contentItems = response.content;
      // this.scrollTop();
    });
  }

  onDisplayOutletContent(outletId) {
    this.outletService.getOutletById(outletId).subscribe(
      (response) => {
        const contact = response ? response.contact : null;
        const website = contact ? contact.webSiteUrl : null;
        const twitter = contact ? contact.twitterUrl : null;
        const outlet = {
          id: outletId,
          name: response.name,
          website: website ? (eq(website, 'cbf') ? null : website) : null,
          twitter: twitter ? (eq(twitter, 'cbf') ? null : twitter) : null,
          picture: this.outletService.getOutletPicture(outletId),
        };

        const facetSearch = new FacetSearch({
          searchBody: new Search({
            ...this.searchBody,
            outlets: [outletId],
          }),
        });

        this.store.dispatch(
          actions.loadOutletContent({
            facetSearch,
            outlet,
          })
        );
      },
      (error) => {
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification('Outlet not found', 'info', 'tl'),
          })
        );
      }
    );
  }

  resetPaginator() {
    if (this.paginator) {
      setTimeout(() => this.paginator.changePage(0));
    }
  }

  private setAuthorSaved() {
    setTimeout(() => {
      this.savedAuthor = find(this.authorsOutletsList, (i) =>
        eq(i.partyId, this.author.id)
      );
      this.isAuthorSaved = !!this.savedAuthor;
      this.starToggle = this.isAuthorSaved ? STAR_ICON_FILL : STAR_ICON;
    }, 500);
  }

  handleSaveAuthor(author) {
    if (this.isAuthorSaved) {
      this.actionType = 'deleting';
      this.store.dispatch(
        actions.deleteAuthorToProject({
          partyId: author.id,
          userProject: this.project,
          author,
        })
      );
    } else {
      this.actionType = 'adding';
      this.store.dispatch(
        actions.addAuthorToProject({
          partyId: author.id,
          userProject: this.project,
          author,
        })
      );
    }
  }

  handleIgnoreAuthor(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveAuthor(this.author);
      return;
    }
    const projectId = this.project?.value || this.project?.id;
    this.userLabelService.getLabel('Ignore', projectId).subscribe({
      next: (ignore) => {
        if (!ignore) {
          this.showLabelError('Ignore');
          return;
        }
        this.store.dispatch(
          actions.addAuthorToProject({
            partyId: this.author.id,
            userProject: this.project,
            author: this.author,
            userLabelId: ignore.id,
          })
        );
      },
      error: () => this.showLabelError('Ignore'),
    });
  }

  handleAuthorLabel(labelName: string) {
    const category = this.findCategory(labelName);
    if (!category || !this.savedAuthor) {
      this.showLabelError(labelName);
      return;
    }
    this.store.dispatch(
      actions.updateAuthorLabel({
        userPartyId: this.savedAuthor.id,
        userProject: this.project,
        author: this.author,
        userLabelId: this.authorLabel === labelName ? null : category.id,
      })
    );
  }

  get isAuthorIgnored(): boolean {
    return this.authorLabel === 'Ignore';
  }

  get authorLabel(): string {
    return (
      this.savedAuthor?.categoryName || this.savedAuthor?.label?.name || null
    );
  }

  get isSavingAuthor(): boolean {
    return this.starToggle === SPINNER_ICON;
  }

  private findCategory(name: string): any {
    return find(
      this.categories,
      (category) => String(category.name).toLowerCase() === name.toLowerCase()
    );
  }

  private showLabelError(labelName: string): void {
    this.store.dispatch(
      actions.showNotification({
        notify: new Notification(
          `The ${labelName} label is not available`,
          'error',
          'tl'
        ),
      })
    );
  }

  openReportMissingDataModal() {
    this.store.dispatch(actions.showReportDialog());
  }

  hasReportingAccess() {
    return this.auth.hasReportingAccess();
  }
}
